import { Cartridge, IMapper, IMapperType, MirrorMode } from '@/Emulator';
import { StaticImplements }                            from '@/LanguageUtilities';

/**
 * Mapper ID 66, also known as GxROM.
 *
 * The designation GxROM refers to Nintendo cartridge boards labeled NES-GNROM
 * and NES-MHROM (and their HVC counterparts), which use discrete logic to
 * provide up to four 32 KB banks of PRG ROM and up to four 8 KB banks of CHR
 * ROM.
 */
@StaticImplements<IMapperType>()
export class Mapper066 implements IMapper
{
    /**
     * @see {@link IMapperType.supports}
     */
    public static supports(cartridge: Cartridge): boolean
    {
        return cartridge.mapperId === 66;
    }

    private prgBankOffset: number = 0;
    private chrBankOffset: number = 0;

    constructor(private readonly cartridge: Cartridge)
    {
    }

    /**
     * @inheritDoc
     */
    public peekCpu(address: number): number
    {
        if (address >= 0x8000 && address <= 0xFFFF) {
            return this.cartridge.prgRom[this.prgBankOffset + (address - 0x8000)];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public peekPpu(address: number): number
    {
        if (address >= 0x0000 && address <= 0x1FFF) {
            return this.cartridge.chrData[this.chrBankOffset + address];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public readCpu(address: number): number
    {
        if (address >= 0x8000 && address <= 0xFFFF) {
            return this.cartridge.prgRom[this.prgBankOffset + (address - 0x8000)];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public readPpu(address: number): number
    {
        if (address >= 0x0000 && address <= 0x1FFF) {
            return this.cartridge.chrData[this.chrBankOffset + address];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public writeCpu(address: number, value: number): void
    {
        if (address >= 0x8000 && address <= 0xFFFF) {
            const prgBank = (value >> 4) & 0x03;
            this.prgBankOffset = prgBank * 32768;

            const chrBank = value & 0x03;
            this.chrBankOffset = chrBank * 8192;
        }
    }

    /**
     * @inheritDoc
     */
    public writePpu(address: number, value: number): void
    {
        if (this.cartridge.hasChrRam && address >= 0x0000 && address <= 0x1FFF) {
            this.cartridge.chrData[this.chrBankOffset + address] = value & 0xFF;
        }
    }

    /**
     * @inheritDoc
     */
    public get hasPendingInterruptRequest(): boolean {
        return false;
    }

    /**
     * @inheritDoc
     */
    public get mirrorMode(): MirrorMode
    {
        return this.cartridge.mirrorMode;
    }

    /**
     * @inheritDoc
     */
    public serialize(): Record<string, any>
    {
        return {};
    }

    /**
     * @inheritDoc
     */
    public deserialize(data: Record<string, any>): void
    {
    }
}
